import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { Box, Button, Card, CircularProgress } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';

import AuthContext from '../../contexts/AuthContext';
import { usePreRegisteredUsers } from '../../contexts/userDataContext';
import { useFullStudentGoals } from '../../contexts/userDataContext';

// Custom hooks
import { useDataPointManagement } from './DataEntryModal/hooks/useDataPointManagement';
import { useDataSubmission } from './DataEntryModal/hooks/useDataSubmission';
import { useGoalStatistics } from './DataEntryModal/hooks/useGoalStatistics';

// New custom hooks for code splitting
import { useDataEntryState } from './hooks/useDataEntryState';
import { useDataEntryEffects } from './hooks/useDataEntryEffects';
import { useDataEntryHandlers } from './hooks/useDataEntryHandlers';
import { useDataEntryMemoized } from './hooks/useDataEntryMemoized';

// Components
import WarningAlerts from './DataEntryModal/WarningAlerts';
import GoalHeader from './DataEntryModal/components/GoalHeader';
import GoalDetails from './DataEntryModal/GoalDetails';
import DataPointList from './DataEntryModal/components/DataPointList';
import FormActions from './DataEntryModal/FormActions';
import AddDataPointButton from './DataEntryModal/components/AddDataPointButton';

// Utils
import {
  getPromptingStructure,
  transformRawDataToFormState,
} from './DataEntryModal/utils/dataUtils';
import { convertCustomDataToCustomSection } from './utils/dataConversion';

import './dataEntry.css';

const DataEntryModal = forwardRef(({
  fromSessionNotes,
  goalId,
  goalTitle,
  selectedGoalIds,
  selectedStudents,
  goalNumber,
  MultipleDataPointsPerSession,
  measurementDuration,
  measurementUnit,
  dataPointPerSession,
  studentDataPoints,
  selectedDate,
  studentId,
  target,
  goalData,
  goalDate,
  open,
  onClose,
  onSuccess,
  goalType,
  subGoalIndex,
  trialsMax,
  allGoalData,
  warning,
  warningKeepOnlyOne,
  warningFromVideo,
  prompting,
  videoId,
  editingDataPoint = null,
  registerModalData,
  entryKey,
  dataEntryRef,
  triggerSave = false,
  draftState = null,
  onDraftChange,
  onDraftClear,
}, ref) => {
  const queryClient = useQueryClient();
  const { user, typeOfUser } = useContext(AuthContext);

  // Data fetching
  const { data: studentGoalsData = [] } = useFullStudentGoals();
  const { data: preRegisteredUsers = [] } = usePreRegisteredUsers();
  // console.log(studentDataPoints,'studentDataPoints');

  // Custom hooks for state management
  const {
    existingEntry,
    isDeleteDialogOpen,
    isDisabled,
    isSaving,
    isDeleting,
    goalNo,
    existingPromptingType,
    criteria,
    promptingType,
    goalPromptingLevel,
    setExistingEntry,
    setIsDeleteDialogOpen,
    setIsDisabled,
    setIsSaving,
    setIsDeleting,
    setGoalNo,
    setExistingPromptingType,
    setCriteria,
    setPromptingType,
    setGoalPromptingLevel,
  } = useDataEntryState({
    editingDataPoint,
    selectedDate,
    studentDataPoints,
    allowNewEntry: true,
  });

  // Custom hooks for data management
  const {
    dataPoints,
    setDataPoints,
    handleDeleteDataPoint,
    handleAddDataPoint,
    handleTrialChange,
    handleAddTrial,
    handleCriteriaChange,
    handleCheckboxChange,
    handleTimeChange,
    handleFieldChange,
    handleAbcChange,
    handlePromptingChange,
    resetDataPoints,
  } = useDataPointManagement(subGoalIndex, promptingType, criteria);

  const { handleSubmit, handleDelete } = useDataSubmission({
    user,
    typeOfUser,
    selectedStudents,
    studentId,
    studentDataPoints,
    selectedDate,
    selectedGoalIds,
    goalTitle,
    goalNo,
    dataPoints,
    videoId,
    goalDate,
    allGoalData,
    queryClient,
    onClose,
    preRegisteredUsers,
    editingDataPoint,
    setIsDisabled,
    setIsSaving,
    setIsDeleting,
    onSuccess,
    fromSessionNotes,
    dataEntryRef,
    entryKey,
  });

  const hasHydratedDraftRef = useRef(false);
  const draftChangeRef = useRef(onDraftChange);
  const lastEmittedDraftRef = useRef(null);

  useEffect(() => {
    draftChangeRef.current = onDraftChange;
  }, [onDraftChange]);

  useEffect(() => {
    if (!open) {
      hasHydratedDraftRef.current = false;
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (!draftState) return;
    if (hasHydratedDraftRef.current) return;
    
    // Don't hydrate draft if we have an editingDataPoint (existing session data)
    // The editingDataPoint prefill should take priority
    if (editingDataPoint) {
      hasHydratedDraftRef.current = true;
      return;
    }

    const { dataPoints: draftDataPoints, goalNo: draftGoalNo } = draftState;

    if (draftGoalNo !== undefined && draftGoalNo !== null) {
      setGoalNo(draftGoalNo);
    }

    if (Array.isArray(draftDataPoints) && draftDataPoints.length > 0) {
      const clonedDataPoints = JSON.parse(JSON.stringify(draftDataPoints));
      setDataPoints(clonedDataPoints);
    }

    hasHydratedDraftRef.current = true;
  }, [draftState, open, setDataPoints, setGoalNo, editingDataPoint]);

  const serializedDraft = useMemo(() => {
    if (!open) return null;

    return {
      goalNo,
      dataPoints: JSON.parse(JSON.stringify(dataPoints)),
    };
  }, [dataPoints, goalNo, open]);

  useEffect(() => {
    if (!serializedDraft) return;
    if (typeof draftChangeRef.current !== 'function') return;

    const draftSignature = JSON.stringify(serializedDraft);
    if (lastEmittedDraftRef.current === draftSignature) {
      return;
    }

    lastEmittedDraftRef.current = draftSignature;

    draftChangeRef.current(serializedDraft);
  }, [serializedDraft]);

  const handleSubmitWithDraft = useCallback(
    async (payload) => {
      const success = await handleSubmit(payload);
      if (success && typeof onDraftClear === 'function') {
        onDraftClear();
      }
      return success;
    },
    [handleSubmit, onDraftClear]
  );

  // Expose save method via ref
  useImperativeHandle(ref, () => ({
    save: async () => {
      const resolvedGoalId = goalId || entryKey?.split('-')?.[0] || null;
      
      // Build the goalInput object with all necessary data for submission
      const goalInput = {
        goalId: goalId,
        goalTitle: goalTitle,
        goalNumber: goalNo,
        dataPoints: dataPoints,
        goalDate: goalDate || selectedDate,
        studentDataPoints: studentDataPoints,
        existingEntry: editingDataPoint || null,
      };

      // Call submit and capture the result
      const success = await handleSubmitWithDraft(goalInput);
      
      // Return the captured data entry ID or throw error if failed
      if (fromSessionNotes && dataEntryRef && resolvedGoalId) {
        const capturedId = dataEntryRef.current?.[resolvedGoalId];
        
        if (capturedId) {
          return { success: true, dataEntryId: capturedId, goalId: resolvedGoalId };
        } else if (success) {
          // Success but no ID captured - this is okay if it was an edit/update
          return { success: true, dataEntryId: null, goalId: resolvedGoalId };
        } else {
          throw new Error(`Failed to save goal data for goal ${resolvedGoalId}`);
        }
      }
      
      return { success, dataEntryId: null, goalId: resolvedGoalId };
    }
  }), [goalId, entryKey, goalTitle, goalNo, dataPoints, goalDate, selectedDate, studentDataPoints, editingDataPoint, fromSessionNotes, dataEntryRef, handleSubmitWithDraft]);

  const { processStudentGoalsData } = useGoalStatistics(
    studentGoalsData,
    typeOfUser
  );

  // Custom hooks for effects, handlers, and memoized values
  useDataEntryEffects({
    allGoalData,
    prompting,
    subGoalIndex,
    criteria,
    setDataPoints,
    setCriteria,
    setPromptingType,
    setGoalPromptingLevel,
    setGoalNo,
    setExistingEntry,
    setExistingPromptingType,
    open,
    editingDataPoint,
    selectedDate,
    studentDataPoints,
    promptingType,
    transformRawDataToFormState,
    getPromptingStructure,
    convertCustomDataToCustomSection,
    processStudentGoalsData,
    studentGoalsData,
    goalNumber,
  });

  const {
    handleOpenDeleteDialog,
    handleCloseDeleteDialog,
    handleConfirmDelete,
  } = useDataEntryHandlers({
    studentDataPoints,
    selectedDate,
    handleDelete,
    resetDataPoints,
    onClose,
    setIsDeleteDialogOpen,
  });

  const { goalCollectionID, displayedSubGoalIndex, subGoalName } =
    useDataEntryMemoized({
      studentGoalsData,
      studentId,
      dataPoints,
      subGoalIndex,
      allGoalData,
      studentDataPoints,
      selectedDate,
    });

  // Register a function to get current data
  useEffect(() => {
    if (registerModalData && entryKey) {
      registerModalData(entryKey, () => {
        // Find the existing entry for this specific goal and date
        const existingEntryForThisGoal = studentDataPoints?.find(
          (dp) => dp.goalId === goalId && dp.date === goalDate
        );

        // Ensure goalId is always available - extract from entryKey if needed
        const finalGoalId = goalId || entryKey?.split('-')[0];

        return {
          goalId: finalGoalId,
          goalTitle,
          goalNumber: goalNo,
          dataPoints: dataPoints, // Current modal points
          studentDataPoints, // Already formatted for submission
          itemGoalDate: goalDate, // Use key expected by handleSubmit
          existingEntry: editingDataPoint || existingEntryForThisGoal || null, // Needed for detecting edits
          goalCollectionId: finalGoalId, // Optional, for Firestore reference
        };
      });
    }
  }, [
    registerModalData,
    entryKey,
    goalId,
    goalTitle,
    goalNo,
    dataPoints,
    goalDate,
    studentDataPoints,
    editingDataPoint,
  ]);

  return (
    <>
      <Card
        sx={{
          backgroundColor: '#e9eaff',
          borderRadius: '8px',
          padding: '0 10px',
        }}
      >
        <Box my={2} className='data-entry-model'>
          <WarningAlerts
            warning={warning}
            warningFromVideo={warningFromVideo}
            warningKeepOnlyOne={warningKeepOnlyOne}
          />

          <GoalHeader
            goalNumber={goalNumber}
            goalNo={goalNo}
            subGoalIndex={displayedSubGoalIndex}
            goalTitle={goalTitle}
            subGoalName={subGoalName}
          />

          <GoalDetails allGoalData={allGoalData} subGoalIndex={subGoalIndex} />

          <DataPointList
            dataPoints={dataPoints}
            goalType={goalType}
            measurementUnit={measurementUnit}
            trialsMax={trialsMax}
            promptingType={promptingType}
            existingPromptingType={existingPromptingType}
            handleDeleteDataPoint={handleDeleteDataPoint}
            handleFieldChange={handleFieldChange}
            handleTrialChange={handleTrialChange}
            handleAddTrial={handleAddTrial}
            handleCriteriaChange={handleCriteriaChange}
            handlePromptingChange={handlePromptingChange}
            handleCheckboxChange={handleCheckboxChange}
            handleAbcChange={handleAbcChange}
            handleTimeChange={handleTimeChange}
            goalPromptingLevel={goalPromptingLevel}
            prompting={prompting}
            allGoalData={allGoalData}
            user={user}
          />

          <AddDataPointButton
            MultipleDataPointsPerSession={MultipleDataPointsPerSession}
            dataPointPerSession={dataPointPerSession}
            handleAddDataPoint={handleAddDataPoint}
          />

          <FormActions
            existingEntry={existingEntry}
            isDeleteDialogOpen={isDeleteDialogOpen}
            isDisabled={isDisabled}
            goalId={goalId}
            goalCollectionID={goalCollectionID}
            handleOpenDeleteDialog={handleOpenDeleteDialog}
            handleCloseDeleteDialog={handleCloseDeleteDialog}
            handleConfirmDelete={handleConfirmDelete}
            handleSubmit={handleSubmitWithDraft}
            isDeleting={isDeleting}
            isSaving={isSaving}
            fromSessionNotes={fromSessionNotes}
          />
        </Box>
      </Card>
    </>
  );
});

export default DataEntryModal;
